import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  materialCatalogFromDatabase,
  materialSubmissionPayload,
  type MaterialApiErrorBody,
  type MaterialDto,
  type MaterialSubmission,
} from './materials'

export class MaterialApiError extends Error {
  constructor(readonly status: number, readonly code?: string, message = 'Não foi possível processar o material.', readonly retryAfter?: number | null) {
    super(message)
  }
}

async function apiResult(response: Response) {
  const result = await response.json().catch(() => ({})) as MaterialApiErrorBody & { materials?: MaterialDto[]; material?: MaterialDto }
  if (!response.ok) throw new MaterialApiError(response.status, result.code, result.message, result.retryAfter)
  return result
}

export async function fetchMaterials() {
  const response = await fetch('/api/materials', { credentials: 'include', cache: 'no-store' })
  const result = await apiResult(response)
  return Array.isArray(result.materials) ? result.materials : []
}

export async function createMaterial(submission: MaterialSubmission) {
  const response = await fetch('/api/materials', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(materialSubmissionPayload(submission)),
  })
  const result = await apiResult(response)
  if (!result.material) throw new MaterialApiError(502, 'INVALID_RESPONSE', 'O servidor não retornou o material cadastrado.')
  return result.material
}

function includeMaterial(materials: MaterialDto[], material: MaterialDto) {
  const key = material.materialName.trim().toLocaleLowerCase('pt-BR')
  return [material, ...materials.filter(item => item.id !== material.id && item.materialName.trim().toLocaleLowerCase('pt-BR') !== key)]
}

export function useMaterialsCatalog() {
  const [materials, setMaterials] = useState<MaterialDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const refresh = useCallback(async (createdMaterial?: MaterialDto) => {
    setLoading(true)
    try {
      const nextMaterials = await fetchMaterials()
      setMaterials(createdMaterial ? includeMaterial(nextMaterials, createdMaterial) : nextMaterials)
      setError('')
    } catch (error) {
      if (createdMaterial) setMaterials(current => includeMaterial(current, createdMaterial))
      setError(error instanceof MaterialApiError && error.code === 'MATERIALS_NOT_CONFIGURED'
        ? 'A integração com o banco de materiais não está configurada neste ambiente.'
        : error instanceof MaterialApiError && error.code === 'MATERIALS_KEY_NOT_PRIVILEGED'
          ? 'A integração com o banco de materiais está sem uma chave de servidor válida.'
        : 'Não foi possível carregar os materiais cadastrados no banco de dados.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  const registerMaterial = useCallback(async (submission: MaterialSubmission) => {
    const createdMaterial = await createMaterial(submission)
    setMaterials(current => includeMaterial(current, createdMaterial))
    await refresh(createdMaterial)
    return createdMaterial
  }, [refresh])

  return {
    catalog: useMemo(() => materialCatalogFromDatabase(materials), [materials]),
    loading,
    error,
    refresh,
    registerMaterial,
  }
}
